import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route } from '@angular/router';
import { Store } from '@ngrx/store';
import { featureAccessAreReady, featureAccessGetAll } from 'mojo-feature-access';
import { Observable, of } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';

/**
 * Guard can check if feature accesses provided in route static data property
 * matches to the user's feature accesses (license feature extensions merged
 * with per-user resources, see /license/feature-access).
 *
 * `featureAccess` requires all listed accesses, `featureAccessAny` requires
 * at least one. A route without either key is not activated.
 *
 * @example
 *    {
 *      path: 'new-reports',
 *      canActivate: [FeatureAccessGuard],
 *      data: {
 *        featureAccess: ["usage-report"]
 *      },
 *    },
 */
@Injectable({ providedIn: 'root' })
export class FeatureAccessGuard {
  constructor(private store: Store<{}>) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.hasFeatureAccess(route.data);
  }

  canLoad(route: Route): Observable<boolean> {
    return this.hasFeatureAccess(route.data);
  }

  private hasFeatureAccess(data: {
    featureAccess?: string | string[];
    featureAccessAny?: string | string[];
  }): Observable<boolean> {
    const required = this.toArray(data.featureAccess);
    const anyOf = this.toArray(data.featureAccessAny);

    if (!required.length && !anyOf.length) {
      return of(false);
    }

    return this.store.select(featureAccessAreReady).pipe(
      filter(Boolean),
      first(),
      switchMap(() => this.store.select(featureAccessGetAll)),
      first(),
      map(
        (accessList: string[]) =>
          required.every((name) => accessList.includes(name)) &&
          (!anyOf.length || anyOf.some((name) => accessList.includes(name))),
      ),
    );
  }

  private toArray(value: string | string[] | undefined): string[] {
    if (!value) {
      return [];
    }
    return Array.isArray(value) ? value : [value];
  }
}
